/**
 * MrShoes product page — gallery, swatch scrollbar, accordion, sticky bar, upsell quick-add
 */

import { initSizeRecommendation } from './c-size-recommendation.js';

const GALLERY_ZOOM_SCALE = 2;

function initGalleryZoom(section) {
  const gallery = section.querySelector('[data-c-pdp-gallery]');
  if (!gallery) return;

  const track = gallery.querySelector('[data-c-pdp-gallery-track]');
  const lightbox = gallery.querySelector('[data-c-pdp-gallery-lightbox]');
  const lightboxItems = [...(lightbox?.querySelectorAll('[data-c-pdp-gallery-lightbox-item]') || [])];
  const lightboxPrev = lightbox?.querySelector('[data-c-pdp-gallery-lightbox-prev]');
  const lightboxNext = lightbox?.querySelector('[data-c-pdp-gallery-lightbox-next]');

  if (lightbox && lightboxItems.length) {
    let lightboxIndex = 0;

    const pauseLightboxMedia = (item) => {
      item?.querySelectorAll('video').forEach((video) => {
        video.pause();
      });
    };

    const updateLightboxNav = () => {
      const atStart = lightboxIndex <= 0;
      const atEnd = lightboxIndex >= lightboxItems.length - 1;

      if (lightboxPrev) {
        lightboxPrev.classList.toggle('is-hidden', atStart);
        lightboxPrev.hidden = atStart;
        lightboxPrev.disabled = atStart;
      }

      if (lightboxNext) {
        lightboxNext.classList.toggle('is-hidden', atEnd);
        lightboxNext.hidden = atEnd;
        lightboxNext.disabled = atEnd;
      }
    };

    const showLightboxItem = (index) => {
      if (!lightboxItems.length) return;
      lightboxIndex = Math.max(0, Math.min(index, lightboxItems.length - 1));

      lightboxItems.forEach((item, i) => {
        const active = i === lightboxIndex;
        item.hidden = !active;
        item.classList.toggle('is-active', active);
        if (!active) pauseLightboxMedia(item);
      });

      updateLightboxNav();
    };

    const closeLightbox = () => {
      if (!lightbox.open) return;
      pauseLightboxMedia(lightboxItems[lightboxIndex]);
      lightbox.close();
    };

    const openLightbox = (index) => {
      if (track?.dataset.dragging === 'true') return;
      showLightboxItem(index);
      if (!lightbox.open) lightbox.showModal();
    };

    gallery.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-c-pdp-gallery-zoom]');
      if (!trigger || !gallery.contains(trigger)) return;
      // Keep native video/model controls usable — only expand control / image frame open lightbox.
      if (event.target.closest('video, iframe, model-viewer, .shopify-model-viewer-ui')) return;
      event.preventDefault();
      const index = Number(trigger.dataset.index);
      openLightbox(Number.isFinite(index) ? index : 0);
    });

    gallery.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const trigger = event.target.closest('[data-c-pdp-gallery-zoom]');
      if (!trigger || !gallery.contains(trigger)) return;
      event.preventDefault();
      const index = Number(trigger.dataset.index);
      openLightbox(Number.isFinite(index) ? index : 0);
    });

    lightboxPrev?.addEventListener('click', (event) => {
      event.stopPropagation();
      showLightboxItem(lightboxIndex - 1);
    });

    lightboxNext?.addEventListener('click', (event) => {
      event.stopPropagation();
      showLightboxItem(lightboxIndex + 1);
    });

    lightbox.addEventListener('keydown', (event) => {
      if (!lightbox.open) return;
      const isRTL = getComputedStyle(document.documentElement).direction === 'rtl';
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        showLightboxItem(lightboxIndex + (isRTL ? 1 : -1));
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        showLightboxItem(lightboxIndex + (isRTL ? -1 : 1));
      }
    });

    lightbox.addEventListener('click', (event) => {
      if (event.target === lightbox) closeLightbox();
    });
    lightbox.querySelector('[data-c-pdp-gallery-lightbox-close]')?.addEventListener('click', closeLightbox);
    lightbox.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeLightbox();
    });
  }

  if (!gallery.classList.contains('c-pdp-gallery--zoom-hover')) return;

  gallery.querySelectorAll('[data-c-pdp-gallery-zoom-frame]').forEach((frame) => {
    const photo = frame.querySelector('.c-pdp-gallery__photo');
    if (!photo) return;

    frame.addEventListener('mousemove', (event) => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const rect = frame.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      photo.style.transformOrigin = `${x}% ${y}%`;
      photo.style.transform = `scale(${GALLERY_ZOOM_SCALE})`;
    });

    frame.addEventListener('mouseleave', () => {
      photo.style.transform = '';
      photo.style.transformOrigin = '';
    });
  });
}

function initPdpGallery(root) {
  const track = root.querySelector('[data-c-pdp-gallery-track]');
  if (!track) return;

  initGalleryZoom(root);

  const slides = [...track.querySelectorAll('[data-c-pdp-gallery-slide]')];
  const thumbs = [...root.querySelectorAll('[data-c-pdp-gallery-thumb]')];
  const prev = root.querySelector('[data-c-pdp-gallery-prev]');
  const next = root.querySelector('[data-c-pdp-gallery-next]');
  // In RTL, horizontal scroll offsets are negative, so flip the sign.
  const isRTL = getComputedStyle(document.documentElement).direction === 'rtl';
  let index = 0;

  const updateNav = () => {
    const atStart = index <= 0;
    const atEnd = index >= slides.length - 1;

    if (prev) {
      prev.classList.toggle('is-hidden', atStart);
      prev.hidden = atStart;
      prev.disabled = atStart;
    }

    if (next) {
      next.classList.toggle('is-hidden', atEnd);
      next.hidden = atEnd;
      next.disabled = atEnd;
    }
  };

  const goTo = (i) => {
    if (!slides.length) return;
    index = Math.max(0, Math.min(i, slides.length - 1));
    track.scrollTo({ left: (isRTL ? -1 : 1) * index * track.clientWidth, behavior: 'smooth' });
    thumbs.forEach((thumb, ti) => {
      thumb.classList.toggle('is-active', ti === index);
      if (ti === index) {
        thumb.setAttribute('aria-current', 'true');
        thumb.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
    updateNav();
  };

  prev?.addEventListener('click', () => goTo(index - 1));
  next?.addEventListener('click', () => goTo(index + 1));

  thumbs.forEach((thumb, ti) => {
    thumb.addEventListener('click', () => goTo(ti));
  });

  track.addEventListener(
    'scroll',
    () => {
      const i = Math.round(Math.abs(track.scrollLeft) / track.clientWidth);
      if (i !== index) {
        index = i;
        thumbs.forEach((thumb, ti) => {
          thumb.classList.toggle('is-active', ti === index);
          if (ti === index) {
            thumb.setAttribute('aria-current', 'true');
            thumb.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
          } else {
            thumb.removeAttribute('aria-current');
          }
        });
        updateNav();
      }
    },
    { passive: true }
  );

  goTo(0);
}

function initSwatchScrollbar(root) {
  const track = root.querySelector('[data-c-pdp-swatches-track]');
  const bar = root.querySelector('[data-c-pdp-swatches-scrollbar]');
  const thumb = root.querySelector('[data-c-pdp-swatches-thumb]');
  if (!track || !bar || !thumb) return;

  // Read the track's own direction (inherits the page direction) so the scroll
  // math matches the actual scrollLeft sign on RTL pages.
  const isRTL = getComputedStyle(track).direction === 'rtl';

  const update = () => {
    const ratio = track.clientWidth / track.scrollWidth;
    if (ratio >= 0.98) {
      bar.classList.add('is-hidden');
      return;
    }
    bar.classList.remove('is-hidden');
    const thumbWidth = Math.max(ratio * bar.clientWidth, 40);
    thumb.style.width = `${thumbWidth}px`;
    const scrollMax = track.scrollWidth - track.clientWidth;
    if (scrollMax <= 0) return;
    const scrollPercent = isRTL ? 1 - -track.scrollLeft / scrollMax : track.scrollLeft / scrollMax;
    const maxOffset = bar.clientWidth - thumbWidth;
    thumb.style.left = `${Math.max(0, Math.min(maxOffset, scrollPercent * maxOffset))}px`;
  };

  track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
  window.addEventListener('resize', update);
  track.querySelectorAll('img').forEach((img) => img.addEventListener('load', update));
  setTimeout(update, 300);

  let dragging = false;
  let startX = 0;
  let startLeft = 0;

  const onThumbMove = (clientX) => {
    if (!dragging) return;
    const maxLeft = bar.clientWidth - thumb.clientWidth;
    const nextLeft = Math.max(0, Math.min(maxLeft, startLeft + (clientX - startX)));
    const ratio = maxLeft > 0 ? nextLeft / maxLeft : 0;
    const scrollMax = track.scrollWidth - track.clientWidth;
    track.scrollLeft = isRTL ? -(1 - ratio) * scrollMax : ratio * scrollMax;
  };

  thumb.addEventListener('mousedown', (e) => {
    e.preventDefault();
    dragging = true;
    startX = e.clientX;
    startLeft = thumb.offsetLeft;
    bar.classList.add('is-dragging');
  });

  document.addEventListener('mouseup', () => {
    dragging = false;
    bar.classList.remove('is-dragging');
  });

  document.addEventListener('mousemove', (e) => onThumbMove(e.clientX));

  bar.addEventListener('click', (e) => {
    if (e.target === thumb) return;
    const clickX = e.clientX - bar.getBoundingClientRect().left;
    const thumbWidth = thumb.clientWidth;
    const maxLeft = bar.clientWidth - thumbWidth;
    const nextLeft = Math.max(0, Math.min(maxLeft, clickX - thumbWidth / 2));
    const ratio = maxLeft > 0 ? nextLeft / maxLeft : 0;
    const scrollMax = track.scrollWidth - track.clientWidth;
    track.scrollLeft = isRTL ? -(1 - ratio) * scrollMax : ratio * scrollMax;
  });

  // Drag scroll on track
  let isDown = false;
  let scrollStart = 0;
  let pointerStart = 0;

  track.addEventListener('mousedown', (e) => {
    if (e.target.closest('a')) return;
    isDown = true;
    track.dataset.dragging = 'false';
    scrollStart = track.scrollLeft;
    pointerStart = e.pageX;
    track.style.cursor = 'grabbing';
  });

  document.addEventListener('mouseup', () => {
    isDown = false;
    track.style.cursor = '';
  });

  track.addEventListener('mousemove', (e) => {
    if (!isDown) return;
    e.preventDefault();
    const walk = (e.pageX - pointerStart) * 1.2;
    if (Math.abs(walk) > 4) track.dataset.dragging = 'true';
    track.scrollLeft = scrollStart - walk;
  });

  track.querySelectorAll('a').forEach((link) => {
    link.addEventListener('click', (e) => {
      if (track.dataset.dragging === 'true') e.preventDefault();
    });
  });
}

function initAccordion(root) {
  root.querySelectorAll('[data-c-pdp-acc-trigger]').forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const item = trigger.closest('[data-c-pdp-acc-item]');
      const isOpen = item?.classList.contains('is-open');
      root.querySelectorAll('[data-c-pdp-acc-item].is-open').forEach((openItem) => {
        if (openItem !== item) {
          openItem.classList.remove('is-open');
          openItem.querySelector('[data-c-pdp-acc-trigger]')?.setAttribute('aria-expanded', 'false');
        }
      });
      item?.classList.toggle('is-open', !isOpen);
      trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    });
  });
}

function initStickyBar(section) {
  const sticky = section.querySelector('[data-c-pdp-sticky]');
  const stickyBtn = section.querySelector('[data-c-pdp-sticky-add]');
  const mainBtn = section.querySelector('.c-pdp__actions-row .add-to-cart-button, .buy-buttons-block .add-to-cart-button, [data-c-pdp-main-add]');
  // Watch the actual buy-buttons row, not the whole details column (which is
  // position:sticky on desktop and never leaves the viewport).
  const anchor =
    section.querySelector('.c-pdp__actions-row, .buy-buttons-block') ||
    mainBtn ||
    section.querySelector('[data-c-pdp-buy-box]');
  if (!anchor || !sticky) return;

  const observer = new IntersectionObserver(
    ([entry]) => {
      // Only show once the buy buttons have scrolled above the viewport,
      // not when they're still below the fold.
      const show = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
    },
    { root: null, threshold: 0 }
  );

  observer.observe(anchor);

  stickyBtn?.addEventListener('click', () => {
    mainBtn?.click();
  });

  if (mainBtn && stickyBtn) {
    const syncDisabled = () => {
      stickyBtn.disabled = mainBtn.disabled;
    };
    syncDisabled();
    new MutationObserver(syncDisabled).observe(mainBtn, { attributes: true, attributeFilter: ['disabled'] });
  }
}

function initUpsellQuickAdd(section) {
  // Multi-variant upsells: forward the compact "+" to the hidden native quick-add
  // "Choose" button so the variant picker modal opens (reuses quick-add.js).
  section.querySelectorAll('[data-c-pdp-upsell-choose]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const choose = btn.closest('product-card')?.querySelector('.quick-add__button--choose');
      if (choose instanceof HTMLElement) choose.click();
    });
  });

  section.querySelectorAll('[data-c-pdp-upsell-add]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const variantId = btn.dataset.variantId;
      if (!variantId || btn.classList.contains('is-added')) return;

      btn.disabled = true;
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
        });
        if (!res.ok) throw new Error('Add failed');
        btn.classList.add('is-added');
        btn.textContent = '✓';
        btn.setAttribute('aria-label', btn.dataset.addedLabel || 'Added');
      } catch {
        btn.disabled = false;
      }
    });
  });
}

function initWishlist(section) {
  section.querySelectorAll('[data-c-pdp-wishlist]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const pressed = btn.getAttribute('aria-pressed') === 'true';
      btn.setAttribute('aria-pressed', pressed ? 'false' : 'true');
      btn.classList.toggle('is-active', !pressed);
    });
  });
}

const COUNTDOWN_CIRCUMFERENCE = 201.06;

const padCountdown = (value) => String(value).padStart(2, '0');

const setCountdownRing = (ring, current, max) => {
  if (!ring || max <= 0) return;
  const ratio = Math.max(0, Math.min(1, current / max));
  ring.style.strokeDashoffset = `${COUNTDOWN_CIRCUMFERENCE * (1 - ratio)}`;
};

function initCountdown(section) {
  section.querySelectorAll('[data-c-countdown]').forEach((timer) => {
    if (timer.dataset.cCountdownReady === 'true') return;
    timer.dataset.cCountdownReady = 'true';

    let mode = timer.dataset.timerMode || 'fake';
    const duration = parseInt(timer.dataset.timerDuration, 10) || 3600;
    const endDate = timer.dataset.endDate || '';
    const endTime = timer.dataset.endTime || '';
    const hideWhenExpired = timer.dataset.hideExpired === 'true';
    const showDays = timer.dataset.showDays === 'true';

    const daysEl = timer.querySelector('[data-c-countdown-days]');
    const hoursEl = timer.querySelector('[data-c-countdown-hours]');
    const minutesEl = timer.querySelector('[data-c-countdown-minutes]');
    const secondsEl = timer.querySelector('[data-c-countdown-seconds]');

    const daysRing = timer.querySelector('[data-c-countdown-progress="days"]');
    const hoursRing = timer.querySelector('[data-c-countdown-progress="hours"]');
    const minutesRing = timer.querySelector('[data-c-countdown-progress="minutes"]');
    const secondsRing = timer.querySelector('[data-c-countdown-progress="seconds"]');

    let endTimestamp = 0;

    const resolveEndTimestamp = () => {
      if (mode === 'fake') {
        const storageKey = `c-pdp-countdown:${timer.id}:${window.location.pathname}`;
        let startTime = parseInt(localStorage.getItem(storageKey), 10);

        if (!startTime || Number.isNaN(startTime)) {
          startTime = Date.now();
          localStorage.setItem(storageKey, String(startTime));
        }

        endTimestamp = startTime + duration * 1000;

        if (Date.now() >= endTimestamp) {
          startTime = Date.now();
          localStorage.setItem(storageKey, String(startTime));
          endTimestamp = startTime + duration * 1000;
        }

        return;
      }

      if (endDate && endTime) {
        endTimestamp = new Date(`${endDate}T${endTime}`).getTime();
      } else if (endDate) {
        endTimestamp = new Date(`${endDate}T23:59:59`).getTime();
      } else {
        mode = 'fake';
        resolveEndTimestamp();
      }
    };

    const showExpiredState = () => {
      if (daysEl) {
        daysEl.textContent = '00';
        setCountdownRing(daysRing, 0, 1);
      }
      if (hoursEl) hoursEl.textContent = '00';
      if (minutesEl) minutesEl.textContent = '00';
      if (secondsEl) secondsEl.textContent = '00';
      setCountdownRing(hoursRing, 0, 24);
      setCountdownRing(minutesRing, 0, 60);
      setCountdownRing(secondsRing, 0, 60);

      if (hideWhenExpired && mode !== 'fake') {
        timer.hidden = true;
      }
    };

    const update = () => {
      const remaining = endTimestamp - Date.now();

      if (remaining <= 0) {
        if (mode === 'fake') {
          resolveEndTimestamp();
          update();
          return;
        }

        showExpiredState();
        return;
      }

      timer.hidden = false;

      const days = Math.floor(remaining / 86400000);
      const hours = Math.floor((remaining % 86400000) / 3600000);
      const minutes = Math.floor((remaining % 3600000) / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);

      if (daysEl) {
        daysEl.textContent = padCountdown(days);
        const totalDays = Math.max(1, Math.ceil(duration / 86400));
        setCountdownRing(daysRing, showDays ? days : 0, totalDays);
      }

      if (hoursEl) hoursEl.textContent = padCountdown(hours);
      if (minutesEl) minutesEl.textContent = padCountdown(minutes);
      if (secondsEl) secondsEl.textContent = padCountdown(seconds);

      setCountdownRing(hoursRing, hours, 24);
      setCountdownRing(minutesRing, minutes, 60);
      setCountdownRing(secondsRing, seconds, 60);
    };

    resolveEndTimestamp();
    update();
    window.setInterval(update, 1000);
  });
}

function initSizeGuideModal(section) {
  section.querySelectorAll('[data-c-pdp-size-guide-open]').forEach((btn) => {
    const modalId = btn.getAttribute('aria-controls');
    const modal = modalId ? document.getElementById(modalId) : btn.closest('[data-c-pdp-section]')?.querySelector('[data-c-pdp-size-guide]');
    if (!modal) return;

    const close = () => {
      modal.close();
      document.documentElement.classList.remove('c-pdp-modal-open');
    };

    btn.addEventListener('click', () => {
      modal.showModal();
      document.documentElement.classList.add('c-pdp-modal-open');
    });

    modal.querySelector('[data-c-pdp-size-guide-close]')?.addEventListener('click', close);
    modal.addEventListener('cancel', () => document.documentElement.classList.remove('c-pdp-modal-open'));
    modal.addEventListener('click', (event) => {
      if (event.target === modal) close();
    });
  });
}

function initPdp(section) {
  if (!section || section.dataset.cPdpReady === 'true') return;
  section.dataset.cPdpReady = 'true';

  initPdpGallery(section);
  initSwatchScrollbar(section);
  initAccordion(section);
  initStickyBar(section);
  initUpsellQuickAdd(section);
  initWishlist(section);
  initCountdown(section);
  initSizeGuideModal(section);
  initSizeRecommendation(section);
}

function initAll(root = document) {
  const scope = root instanceof Element ? root : document;
  scope.querySelectorAll('[data-c-pdp-section]').forEach(initPdp);
}

initAll(document);

document.addEventListener('shopify:section:load', (event) => {
  initAll(event.target);
});

export { initPdp };
